import { logError } from '../logging';
import type { Placement } from '../placement';
import type {} from './csa-global';

/**
 * Attaches metadata from an `entity` to subsequent events. For example:
 * ```
 *   csa.addCsaEntity({ ABPStatus: ABP_STATUS[data.abpStatus as keyof typeof ABP_STATUS] });
 *   events('log', {schemaId: 'ApeSafeframe.csaEvent.1'}, {ent: 'all'})
 * ```
 *
 * will produce the following CSA log:
 * ```
 * "data": {
 *      "schemaId": "ApeSafeframe.csaEvent.1",
 *      "metricName": "abpstatus_notenabled:Gateway_right-2_desktop:4293fb4d-96ae-46e9-81f1-7503c09f76f3",
 *      "metricValue": 1,
 *      ...
 *      "entities": {
 *          "adCreativeMetaData": {
 *              "id": "3v21xq-3ejsvn-ut5yxw-3kbtb4",
 *              "ABPStatus": "NotEnabled"
 *          },
 *          ...
 *      }
 * ```
 * @param entity {Object} - metadata that can be attached to multiple events
 */

export const setCsaEntity = (entity: object): void => {
    if (!window.csa) return;

    try {
        window.csa('Events', {
            producerId: 'adplacements'
        })('setEntity', { adCreativeMetaData: entity });
    } catch (ex) {
        logError("Error with 'setEntity' CSA", ex);
    }
};

/**
 * Increment counter for a CSA metric. For example:
 * ```
 * logCsaEvent("adload:htmlreached", "Gateway_desktop-ad-center-1_desktop", "73950e1c-6f66-4784-b2d9-e5d5693a4353");
 * ```
 *
 * will produce the following CSA log:
 * ```
 * "data": {
 *      "schemaId": "ApeSafeframe.csaEvent.1",
 *      "metricName": "adload:htmlreached:Gateway_desktop-ad-center-1_desktop:73950e1c-6f66-4784-b2d9-e5d5693a4353",
 *      "metricValue": 1,
 *      ...
 * }
 * ```
 *
 * CSA events reference: https://info.analytics.a2z.com/#/docs/data_collection/csa/events
 */
export const incrementCsaPlacementCounter = (metric: string, placement: Placement, count = 1) => {
    if (!window.csa) return;

    try {
        window.csa('Events', {
            producerId: 'adplacements'
        })(
            'log',
            {
                schemaId: 'ApeSafeframe.csaEvent.1',
                metricName: metric + ':' + placement.name + ':' + placement.id,
                metricValue: count
            },
            { ent: 'all' }
        );
    } catch (ex) {
        logError("Error with 'logCsaEvent' CSA", ex);
    }
};

export const incrementCsaCounter = (metric: string, count = 1) => {
    if (!window.csa) return;

    try {
        window.csa('Events', {
            producerId: 'adplacements'
        })(
            'log',
            {
                schemaId: 'ApeSafeframe.csaEvent.1',
                metricName: metric,
                metricValue: count
            },
            { ent: 'all' }
        );
    } catch (ex) {
        logError("Error with 'incrementCounter' CSA", ex);
    }
};
